import { Order as OrderTypes } from '@luxuryescapes/contract-svc-order'

function mapMerchandiseVariant(
  variant: any,
  images: Array<App.Image>,
): App.MerchandiseOfferVariant {
  let variantImages = images
  if (variant.images.length > 0) {
    variantImages = variant.images.map((image: any): App.Image => ({
      id: image.imageId,
      title: image.alt ?? undefined,
    }))
  }

  return {
    id: variant.id,
    sku: variant.sku ?? undefined,
    label: variant.label,
    price: variant.price,
    regularPrice: variant.basePrice,
    pointsPrice: variant.pointPrice,
    pointsRegularPrice: variant.pointBasePrice,
    images: variantImages,
    loyalty: {
      productType: variant.loyalty.productType,
    },
  }
}

function mapSizeChart(sizeChart: any): App.MerchandiseOfferSizing | undefined {
  if (!sizeChart) {
    return undefined
  }

  if (typeof sizeChart.data === 'string') {
    return {
      image: {
        id: sizeChart.data,
      }
    }
  } else {
    return {
      charts: Object.entries(sizeChart.data).map(([name, sizeOptions]: [string, Array<any>]): App.MerchandiseOfferSizeChart => {
        return {
          name,
          headings: Object.keys(sizeOptions[0]),
          rows: sizeOptions.map((sizeOption) => Object.values(sizeOption)),
        }
      }),
    }
  }
}

/**
 * Maps the snapshot on the item into a order merchandise snapshot
 * This snapshot attempts to match the existing merchandise offer
 *
 * There are no types available from the server, so, lets hope it stays good /shrug
 */
function mapSnapshot(serverMerchandise: any): App.OrderMerchandiseSnapshot {
  const images: Array<App.Image> = []

  if (serverMerchandise.imageId) {
    images.push({ id: serverMerchandise.imageId })
  }

  if (serverMerchandise.images.length > 0) {
    images.push(...serverMerchandise.images.map((image: any): App.Image => ({
      id: image.imageId,
      title: image.alt ?? undefined,
    })))
  }

  return {
    type: 'merchandise',
    parentType: 'merchandise',
    analyticsType: 'merchandise',
    productType: 'merchandise',
    id: serverMerchandise.id,
    slug: serverMerchandise.slug ?? '',
    seller: {
      id: serverMerchandise.seller.id,
      name: serverMerchandise.seller.businessName,
      emailAddress: serverMerchandise.seller.emailAddress,
      shipping: {
        minDays: serverMerchandise.seller.shippingSLA.minimum,
        maxDays: serverMerchandise.seller.shippingSLA.maximum,
      }
    },
    luxPlus: {},
    name: serverMerchandise.name,
    category: serverMerchandise.category ? {
      id: serverMerchandise.category.id,
      slug: serverMerchandise.category.slug,
      name: serverMerchandise.category.displayName,
      parentId: serverMerchandise.category.parentId ?? undefined,
    } : undefined,
    brand: {
      id: serverMerchandise.brand.id,
      name: serverMerchandise.brand.name ?? '',
    },
    images,
    variant: mapMerchandiseVariant(serverMerchandise.variant, images),
    copy: {
      description: serverMerchandise.description ?? undefined,
      features: serverMerchandise.productFeatures ?? undefined,
      specifications: serverMerchandise.specifications ?? undefined,
      shippingPolicy: serverMerchandise.seller.shippingPolicy ?? undefined,
      returnPolicy: serverMerchandise.seller.returnPolicy ?? undefined,
    },
    sizing: mapSizeChart(serverMerchandise.sizeMatrix),
  }
}

function mapMerchandiseOrderItem(item: OrderTypes.MerchandiseItem, orderId: string): App.OrderMerchandiseItem {
  return {
    id: item.id,
    orderId,
    total: item.total,
    status: item.status,
    offerId: item.display_cache?.id as string,
    variantId: (item.display_cache?.variant as { id: string })?.id,
    sellerId: (item.display_cache as unknown as App.MerchandiseOffer)?.seller?.id,
    snapshot: mapSnapshot(item.display_cache),
    shippingGroupId: (item as any).shipping_group,
  }
}

export default mapMerchandiseOrderItem
